import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, type Dispatch, type SetStateAction } from "react"
import type { BenefitEditorController } from "./benefitEditorController"
import { benefitEditorActions, commandKey } from "./benefitEditorSlice"
import { editorFieldChange, editorFieldValue, hasEditorField, type DraftGroup, type DraftField, type FieldsFor } from "./benefitEditorDrafts"

export const BenefitEditorContext = createContext<BenefitEditorController | null>(null)
const idle = Symbol("no-active-draft-field")
const noSubscribe = () => () => {}

/** Typed React setter contract over a field of the active Redux draft; no saved DTO mirror. */
export function useBenefitDraftState<G extends DraftGroup, F extends DraftField<G>>(
  group: G, field: F, initial: NoInfer<FieldsFor<G>[F]> | (() => NoInfer<FieldsFor<G>[F]>),
): [FieldsFor<G>[F], Dispatch<SetStateAction<FieldsFor<G>[F]>>] {
  type T = FieldsFor<G>[F]
  const context = useContext(BenefitEditorContext)
  // Shared controls outside a benefit workspace still have their caller-owned lifecycle.
  // Inside it, this is only the unopened form's initialization value, never an active draft.
  const [closedValue, setClosedValue] = useState(initial)
  const closed = useRef(closedValue); closed.current = closedValue
  const initializer = useRef(initial); initializer.current = initial
  const getActive = () => {
    const active = context?.owned()
    return active?.group === group && hasEditorField(active.kind, field) ? active : null
  }
  const getValue = () => {
    const active = getActive()
    return active && Object.hasOwn(active.fields, field) ? editorFieldValue(active, group, field) : idle
  }
  const value = useSyncExternalStore<T | typeof idle | undefined>(context?.store.subscribe ?? noSubscribe, getValue, () => idle)
  const sessionId = useSyncExternalStore(context?.store.subscribe ?? noSubscribe, () => getActive()?.sessionId ?? null, () => null)
  const previousSession = useRef<string | null>(null)
  useLayoutEffect(() => {
    if (previousSession.current && !sessionId) {
      const fresh = typeof initializer.current === "function" ? (initializer.current as () => T)() : initializer.current
      closed.current = fresh
      setClosedValue(() => fresh)
    }
    previousSession.current = sessionId
  }, [sessionId])
  useLayoutEffect(() => context?.register(group, field, {
    value: () => closed.current,
    fresh: () => typeof initializer.current === "function" ? (initializer.current as () => T)() : initializer.current,
  }), [context, group, field])
  useLayoutEffect(() => {
    const active = getActive()
    if (!context || !active || active.sessionId !== sessionId) return
    const change = editorFieldChange(active, group, field, structuredClone(closed.current))
    if (change) context.store.dispatch(benefitEditorActions.fieldInitialized(change))
  }, [context, sessionId, group, field])
  const setValue = useCallback<Dispatch<SetStateAction<T>>>(update => {
    const active = context?.owned()
    if (active?.group === group && hasEditorField(active.kind, field)) {
      if (!["opening", "editing"].includes(active.phase)) return
      const previous = Object.hasOwn(active.fields, field) ? editorFieldValue(active, group, field) as T : closed.current
      const next = typeof update === "function" ? (update as (value: T) => T)(previous) : update
      const change = editorFieldChange(active, group, field, next)
      if (change) context!.store.dispatch(benefitEditorActions.fieldChanged(change))
    } else {
      // Existing open handlers initialize fields immediately before requesting the editor.
      // Keep that same-event value visible to the registry, not the previous render's value.
      const next = typeof update === "function" ? (update as (value: T) => T)(closed.current) : update
      closed.current = next
      setClosedValue(next)
    }
  }, [context, group, field])
  return [value === idle ? closedValue : value as T, setValue]
}

/** Existing financial code may keep synchronous ref access; the value itself lives in the draft. */
export function useBenefitDraftRef<G extends DraftGroup, F extends DraftField<G>>(group: G, field: F, initial: NoInfer<FieldsFor<G>[F]>): { current: FieldsFor<G>[F] } {
  type T = FieldsFor<G>[F]
  const context = useContext(BenefitEditorContext)
  const [value, setValue] = useBenefitDraftState(group, field, initial)
  const local = useRef(value); local.current = value
  return useMemo(() => ({
    get current() {
      const active = context?.owned()
      return active?.group === group && hasEditorField(active.kind, field) && Object.hasOwn(active.fields, field) ? editorFieldValue(active, group, field) as T : local.current
    },
    set current(next: T) { local.current = next; setValue(next) },
  }), [context, group, field, setValue])
}

/** Pending UI survives a form unmount; only the accepted command controls the write gate. */
export function useBenefitSaving(): [boolean, Dispatch<SetStateAction<boolean>>] {
  const context = useContext(BenefitEditorContext)
  const [local, setLocal] = useState(false)
  const pending = useSyncExternalStore(context?.store.subscribe ?? noSubscribe, () =>
    !!context && context.store.getState().benefitEditors.commands[commandKey(context.address)]?.phase === "saving", () => false)
  return [pending || local, setLocal]
}

/** The workspace comparison owns the modal surface while a conflict/unknown outcome is reviewed. */
export function useBenefitReviewActive() {
  const context = useContext(BenefitEditorContext)
  return useSyncExternalStore(context?.store.subscribe ?? noSubscribe, () => {
    const phase = context?.owned()?.phase
    return phase === "conflict" || phase === "outcomeUnknown"
  }, () => false)
}

export function useWorkspaceEditorActivity() {
  const context = useContext(BenefitEditorContext)
  return useSyncExternalStore(context?.store.subscribe ?? noSubscribe, () => {
    if (!context) return false
    const state = context.store.getState().benefitEditors
    return !!state.active || Object.values(state.commands).some(command => ["saving", "outcomeUnknown"].includes(command.phase))
  }, () => false)
}
